import { useParams, Link } from 'react-router-dom';
import SEOHead from '@/components/base/SEOHead';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import JsonLd from '@/components/base/JsonLd';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ArticleCard from '@/components/feature/ArticleCard';
import { categories, articles } from '@/mocks/siteData';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);

  if (!category) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="font-heading text-2xl font-bold text-foreground-800 mb-3">分类未找到</h1>
          <Link to="/" className="text-primary-500 hover:text-primary-600 transition-colors">返回首页</Link>
        </div>
      </div>
    );
  }

  const categoryArticles = articles.filter((a) => a.category === slug);

  const schemaData = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${category.name} - 猫咪视频大全`,
    description: category.description,
    url: `https://catvideo.cn/category/${slug}`,
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: categoryArticles.map((article, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        url: `https://catvideo.cn/article/${article.id}`,
      })),
    },
  };

  return (
    <>
      <ScrollToTop />
      <SEOHead
        title={`${category.name} | 猫咪视频大全`}
        description={category.description}
        keywords={`${category.name},猫咪视频,${category.name}合集,猫咪视频大全`}
        canonicalUrl={`https://catvideo.cn/category/${slug}`}
        ogImage="https://readdy.ai/api/search-image?query=Cute%20cats%20playing%20together%20in%20a%20warm%20cozy%20room%20soft%20natural%20lighting%20cream%20and%20beige%20tones%20peaceful%20atmosphere%20pet%20photography&width=1200&height=630&seq=og-category&orientation=landscape"
        ogImageWidth={1200}
        ogImageHeight={630}
      />
      <JsonLd data={schemaData} />

      <div className="min-h-screen">
        <Navbar />
        <main className="pt-16">
          <div className="bg-accent-50">
            <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-10 md:py-14">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: category.name },
                ]}
              />
              <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-800 mb-3">
                {category.name}
              </h1>
              <p className="text-foreground-500 text-sm md:text-base max-w-2xl">
                {category.description}
              </p>
            </div>
          </div>

          <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-10 md:py-14">
            {categoryArticles.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                {categoryArticles.map((article) => (
                  <ArticleCard
                    key={article.id}
                    id={article.id}
                    title={article.title}
                    excerpt={article.excerpt}
                    categorySlug={article.category}
                    categoryName={category.name}
                    imageUrl={article.imageUrl}
                    date={article.date}
                    readTime={article.readTime}
                    views={article.views}
                    featured={false}
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-20 h-20 mx-auto mb-5 rounded-full bg-background-100 flex items-center justify-center">
                  <i className="ri-inbox-line text-3xl text-foreground-300"></i>
                </div>
                <h2 className="font-heading text-xl font-bold text-foreground-700 mb-2">暂无内容</h2>
                <p className="text-foreground-500 text-sm mb-6">该分类下暂时没有内容，请稍后再来</p>
                <Link
                  to="/"
                  className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
                >
                  <i className="ri-arrow-left-line"></i>
                  返回首页
                </Link>
              </div>
            )}
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}